import type { QueryKey } from '@tanstack/react-query';
import {
  FLOW_CONTACT_STATE_KEY,
  FLOW_CONTACT_STATE_DETAIL_KEY,
  FLOW_CONTACT_SIGNALS_KEY,
  FLOW_ACCOUNT_SIGNALS_KEY,
} from '../../contact-state-keys';
// Root only, imported from ./keys (never ./hooks) — see keys.ts's doc
// comment: this queue context must not import a hooks module for a key
// root (plan 2026-09-09-003 D14).
import { FLOW_PLAYS_QUERY_KEY } from '../../plays/keys';

/**
 * The key list `EnrollQueueContext.tsx`'s debounced `invalidateContactState`
 * loops over on every queue add/patch/remove and both send-settle points.
 *
 * Extracted out of that 2000+ line file (not just left as an inline array in
 * the callback) so this list has its own pure, dependency-light test —
 * `__tests__/contact-state-invalidation-keys.test.ts` — independent of
 * mounting the full `EnrollQueueProvider`.
 *
 * A queue add/send/remove changes a play's `enrollment_items` membership
 * rows, so the three "People in <Play>" sections (Responded / Not Responded
 * / Not in this play yet) must refetch to reflect it. Plan
 * `2026-09-25-002-plays-sections-unify-accordion-plan.md` U7 (R9): those
 * three sections are now `ContactFlowTable` / `useFlowContacts` mounts,
 * keyed under `['flow-contacts', …]` — not `PlayPeopleTable` /
 * `usePlayPeopleQueries` (`PLAY_PEOPLE_QUERY_KEY`, retired). The
 * `['flow-contacts']` root entry below already invalidates every mount
 * whose query key starts with it, so no play-people-specific key is needed.
 */
export const CONTACT_STATE_INVALIDATION_KEYS: readonly QueryKey[] = [
  FLOW_CONTACT_STATE_KEY,
  FLOW_CONTACT_STATE_DETAIL_KEY,
  FLOW_CONTACT_SIGNALS_KEY,
  FLOW_ACCOUNT_SIGNALS_KEY,
  FLOW_PLAYS_QUERY_KEY,
  // Codex #1 (2026-09-10 retry) — see EnrollQueueContext.tsx's inline
  // comment on the original inline loop for the full history.
  ['flow-contacts'],
];
